import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  Mail, Settings, Link2, Unlink, RefreshCw, Loader2, 
  Inbox, Send, FileText, Clock, CheckCircle2, XCircle,
  ChevronRight, ExternalLink, Paperclip, User, Calendar,
  Timer, DollarSign, Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailIntegration = ({ onEmailFiled }) => {
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [providers, setProviders] = useState({});
  const [emails, setEmails] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetail, setEmailDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [cases, setCases] = useState([]);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [fileEmailData, setFileEmailData] = useState({
    case_id: '',
    create_time_entry: false,
    time_entry_minutes: 6,
    time_entry_description: '',
    activity_type: 'review'
  });
  const [filing, setFiling] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const fetchConnections = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-integration/connections`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnections(data.connections || []);
        setProviders(data.providers || {});
        
        // If we have a connection, select it and fetch emails
        if (data.connections?.length > 0) {
          const firstConnection = data.connections[0];
          setSelectedProvider(firstConnection.provider);
        }
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
      toast.error('Failed to load email connections');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCases = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/case-management/cases`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCases(data.cases || []);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
    fetchCases();
  }, [fetchConnections, fetchCases]);

  const fetchEmails = useCallback(async (provider, folder = 'inbox') => {
    if (!provider) return;
    setLoadingEmails(true);
    try {
      const response = await fetch(
        `${API_URL}/api/email-integration/emails?provider=${provider}&folder=${folder}&top=20`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEmails(data.messages || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fetch emails');
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Failed to fetch emails');
    } finally {
      setLoadingEmails(false);
    }
  }, []);

  useEffect(() => {
    if (selectedProvider && connections.some(c => c.provider === selectedProvider)) {
      fetchEmails(selectedProvider);
    }
  }, [selectedProvider, connections, fetchEmails]);

  const fetchEmailDetail = async (provider, messageId) => {
    setLoadingDetail(true);
    try {
      const response = await fetch(
        `${API_URL}/api/email-integration/emails/${provider}/${messageId}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEmailDetail(data);
      }
    } catch (error) {
      console.error('Error fetching email detail:', error);
      toast.error('Failed to load email');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleConnect = async (provider) => {
    try {
      const response = await fetch(`${API_URL}/api/email-integration/connect/${provider}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Redirect to OAuth URL
        window.location.href = data.authorization_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start connection');
      }
    } catch (error) {
      console.error('Error connecting:', error);
      toast.error('Failed to connect');
    }
  };

  const handleDisconnect = async (provider) => {
    setDisconnecting(provider);
    try {
      const response = await fetch(`${API_URL}/api/email-integration/disconnect/${provider}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        toast.success(`${provider === 'outlook' ? 'Outlook' : 'Gmail'} disconnected`);
        setConnections(connections.filter(c => c.provider !== provider));
        if (selectedProvider === provider) {
          setSelectedProvider(null);
          setEmails([]);
        }
      }
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleSelectEmail = (email) => {
    setSelectedEmail(email);
    fetchEmailDetail(email.provider, email.id);
  };

  const openFileDialog = (email) => {
    setSelectedEmail(email);
    setFileEmailData({
      case_id: '',
      create_time_entry: false,
      time_entry_minutes: 6,
      time_entry_description: `Reviewed email: ${email.subject}`,
      activity_type: 'review'
    });
    setFileDialogOpen(true);
  };

  const handleFileEmail = async () => {
    if (!fileEmailData.case_id) {
      toast.error('Please select a case');
      return;
    }
    
    setFiling(true);
    try {
      const response = await fetch(`${API_URL}/api/email-integration/file-to-case`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email_id: selectedEmail.id,
          case_id: fileEmailData.case_id,
          create_time_entry: fileEmailData.create_time_entry,
          time_entry_minutes: fileEmailData.create_time_entry ? fileEmailData.time_entry_minutes : null,
          time_entry_description: fileEmailData.create_time_entry ? fileEmailData.time_entry_description : null,
          activity_type: fileEmailData.activity_type
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setFileDialogOpen(false);
        if (onEmailFiled) onEmailFiled(result);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to file email');
      }
    } catch (error) {
      toast.error('Failed to file email');
    } finally {
      setFiling(false);
    }
  };

  const getProviderIcon = (provider) => {
    if (provider === 'outlook') {
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
          <path d="M24 7.387v10.478c0 .23-.08.424-.238.583a.79.79 0 01-.585.238h-8.13v-6.522l1.093.915a.37.37 0 00.267.105.395.395 0 00.282-.12l.43-.47a.378.378 0 00.105-.267.396.396 0 00-.12-.282L14.5 9.69l-.53-.48-.007-.005-.058-.052-.084-.07-.038-.028a.728.728 0 00-.044-.033l-.042-.027-.035-.022-.023-.012-.04-.022-.044-.022-.018-.008-.044-.019-.05-.02-.015-.005-.044-.014-.055-.016-.02-.005-.04-.01-.062-.012-.03-.005-.033-.005-.068-.007-.038-.002h-.068l-.038.002-.068.007-.033.005-.03.005-.062.012-.04.01-.02.005-.055.016-.044.014-.015.005-.05.02-.044.019-.018.008-.044.022-.04.022-.023.012-.035.022-.042.027a.728.728 0 00-.044.033l-.038.028-.084.07-.058.052-.007.005-.53.48-2.604 2.355a.396.396 0 00-.12.282.378.378 0 00.105.267l.43.47a.395.395 0 00.282.12.37.37 0 00.267-.105l1.093-.915v6.522H.822a.79.79 0 01-.584-.238A.79.79 0 010 17.865V7.387c0-.23.08-.424.238-.584a.79.79 0 01.584-.238h8.13V0h14.225c.23 0 .425.08.585.238.158.16.238.354.238.585v5.742h-8.953v.002H8.952v.238z" fill="#0078D4"/>
          <path d="M8.953 6.565v12.087H.822a.79.79 0 01-.584-.238A.79.79 0 010 17.83V7.387c0-.23.08-.424.238-.584a.79.79 0 01.584-.238h8.13z" fill="#0078D4"/>
          <path d="M8.953.822v17.83H.822a.79.79 0 01-.584-.238A.79.79 0 010 17.83V.822C0 .593.08.398.238.238A.79.79 0 01.822 0h7.309c.23 0 .424.08.584.238.158.16.238.354.238.584z" fill="#0364B8"/>
          <path d="M4.477 6.696c1.76 0 3.186 1.568 3.186 3.5s-1.427 3.5-3.186 3.5c-1.76 0-3.186-1.568-3.186-3.5s1.426-3.5 3.186-3.5zm0 1.565c-.908 0-1.644.866-1.644 1.935 0 1.07.736 1.935 1.644 1.935.908 0 1.644-.866 1.644-1.935 0-1.07-.736-1.935-1.644-1.935z" fill="#fff"/>
        </svg>
      );
    } else {
      return (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
        </svg>
      );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Outlook Card */}
        <Card className={`border-2 ${connections.some(c => c.provider === 'outlook') ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200'}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getProviderIcon('outlook')}
                <div>
                  <CardTitle className="text-lg">Microsoft Outlook</CardTitle>
                  <CardDescription className="text-xs">Office 365 & Outlook.com</CardDescription>
                </div>
              </div>
              {connections.some(c => c.provider === 'outlook') && (
                <Badge className="bg-green-500">Connected</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {connections.find(c => c.provider === 'outlook') ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4" />
                  {connections.find(c => c.provider === 'outlook')?.email}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedProvider('outlook');
                      fetchEmails('outlook');
                    }}
                    className="flex-1"
                  >
                    <Inbox className="w-4 h-4 mr-2" />
                    View Emails
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDisconnect('outlook')}
                    disabled={disconnecting === 'outlook'}
                  >
                    {disconnecting === 'outlook' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ) : providers.outlook?.configured ? (
              <Button onClick={() => handleConnect('outlook')} className="w-full bg-blue-600 hover:bg-blue-700">
                <Link2 className="w-4 h-4 mr-2" />
                Connect Outlook
              </Button>
            ) : (
              <p className="text-sm text-amber-600">OAuth credentials not configured</p>
            )}
          </CardContent>
        </Card>

        {/* Gmail Card */}
        <Card className={`border-2 ${connections.some(c => c.provider === 'gmail') ? 'border-red-500 bg-red-50/50' : 'border-gray-200'}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getProviderIcon('gmail')}
                <div>
                  <CardTitle className="text-lg">Gmail</CardTitle>
                  <CardDescription className="text-xs">Google Workspace & Gmail</CardDescription>
                </div>
              </div>
              {connections.some(c => c.provider === 'gmail') && (
                <Badge className="bg-green-500">Connected</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {connections.find(c => c.provider === 'gmail') ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4" />
                  {connections.find(c => c.provider === 'gmail')?.email}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedProvider('gmail');
                      fetchEmails('gmail');
                    }}
                    className="flex-1"
                  >
                    <Inbox className="w-4 h-4 mr-2" />
                    View Emails
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDisconnect('gmail')}
                    disabled={disconnecting === 'gmail'}
                  >
                    {disconnecting === 'gmail' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ) : providers.gmail?.configured ? (
              <Button onClick={() => handleConnect('gmail')} className="w-full bg-red-600 hover:bg-red-700">
                <Link2 className="w-4 h-4 mr-2" />
                Connect Gmail
              </Button>
            ) : (
              <p className="text-sm text-amber-600">OAuth credentials not configured</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email List */}
      {selectedProvider && connections.some(c => c.provider === selectedProvider) && (
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getProviderIcon(selectedProvider)}
                <CardTitle>
                  {selectedProvider === 'outlook' ? 'Outlook' : 'Gmail'} Inbox
                </CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchEmails(selectedProvider)}
                disabled={loadingEmails}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingEmails ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loadingEmails ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : emails.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                <Inbox className="w-12 h-12 mb-2 opacity-30" />
                <p>No emails found</p>
              </div>
            ) : (
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {emails.map((email) => (
                  <div
                    key={email.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${!email.is_read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => handleSelectEmail(email)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-medium text-sm truncate ${!email.is_read ? 'font-semibold' : ''}`}>
                            {email.from_name || email.from_address}
                          </span>
                          {email.has_attachments && (
                            <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                        <p className={`text-sm truncate ${!email.is_read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {email.subject || '(No subject)'}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-1">
                          {email.body_preview}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-500">
                          {formatDate(email.received_at)}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7 px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            openFileDialog(email);
                          }}
                        >
                          <Briefcase className="w-3 h-3 mr-1" />
                          File to Case
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Detail Dialog */}
      <Dialog open={!!emailDetail} onOpenChange={() => setEmailDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {emailDetail?.subject || '(No subject)'}
            </DialogTitle>
            <DialogDescription>
              From: {emailDetail?.from_name || emailDetail?.from_address}
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : emailDetail && (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 border-b pb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(emailDetail.received_at).toLocaleString()}
                </div>
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  To: {emailDetail.to_addresses?.join(', ')}
                </div>
                {emailDetail.cc_addresses?.length > 0 && (
                  <div className="flex items-center gap-1">
                    CC: {emailDetail.cc_addresses.join(', ')}
                  </div>
                )}
              </div>
              
              <div className="prose prose-sm max-w-none">
                {emailDetail.body_html ? (
                  <div dangerouslySetInnerHTML={{ __html: emailDetail.body_html }} />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-sm">
                    {emailDetail.body_text}
                  </pre>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEmailDetail(null)}>
              Close
            </Button>
            <Button onClick={() => {
              setEmailDetail(null);
              openFileDialog(emailDetail);
            }}>
              <Briefcase className="w-4 h-4 mr-2" />
              File to Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File to Case Dialog */}
      <Dialog open={fileDialogOpen} onOpenChange={setFileDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              File Email to Case
            </DialogTitle>
            <DialogDescription>
              {selectedEmail?.subject}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Case *</Label>
              <Select
                value={fileEmailData.case_id}
                onValueChange={(value) => setFileEmailData({...fileEmailData, case_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a case..." />
                </SelectTrigger>
                <SelectContent>
                  {cases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.case_number} - {c.case_name || c.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
              <Checkbox
                id="create_time_entry"
                checked={fileEmailData.create_time_entry}
                onCheckedChange={(checked) => setFileEmailData({...fileEmailData, create_time_entry: checked})}
              />
              <label htmlFor="create_time_entry" className="text-sm font-medium cursor-pointer">
                Create billable time entry
              </label>
            </div>

            {fileEmailData.create_time_entry && (
              <div className="space-y-4 p-4 border rounded-lg bg-blue-50/50">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Minutes</Label>
                    <Input
                      type="number"
                      min="1"
                      value={fileEmailData.time_entry_minutes}
                      onChange={(e) => setFileEmailData({...fileEmailData, time_entry_minutes: parseInt(e.target.value) || 6})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Activity Type</Label>
                    <Select
                      value={fileEmailData.activity_type}
                      onValueChange={(value) => setFileEmailData({...fileEmailData, activity_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="read">Read Email</SelectItem>
                        <SelectItem value="review">Review Email</SelectItem>
                        <SelectItem value="compose">Compose Email</SelectItem>
                        <SelectItem value="send">Send Email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={fileEmailData.time_entry_description}
                    onChange={(e) => setFileEmailData({...fileEmailData, time_entry_description: e.target.value})}
                    placeholder="Describe the work..."
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFileDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleFileEmail} disabled={filing || !fileEmailData.case_id}>
              {filing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              File Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailIntegration;
